import { useNavigate, useParams } from 'react-router';
import { useMutation } from '@tanstack/react-query';
import { AuthTitle } from '@/bundle/Auth/ui/AuthTitle/AuthTitle';
import { amplitudeService } from '@/services/amplitudeService/amplitudeService';
import { AMPLITUDE_EVENTS } from '@/services/amplitudeService/amplitudeEvents';
import { useFormik } from 'formik';
import { passwordValidationSchema } from './const/passwordValidationSchema';
import { setPassword } from './api';
import { Notification } from '@/components/Notification/Notification';
import { AuthLayout } from '@/bundle/Auth/ui/AuthLayout/AuthLayout';
import { AuthStep } from '@/bundle/Auth/ui/AuthStep/AuthStep';
import { ACTIVATION_TOKEN_INVALID_RESPONSE } from '@/api/const';
import { useEffect } from 'react';
import { getSetPasswordLinkExpiredPath } from '../SetPasswordLinkExpiredPage/path/path';
import { getResponseError } from '@/helpers/apiHelpers/responseHelpers';
import { getPasswordChangedPath } from '../../pages/PasswordChangedPage/path/path';
import { SetPasswordForm } from '@/bundle/shared/components/SetPasswordForm/SetPasswordForm';

type PasswordConfirmationFormType = { password: string; passwordConfirmation: string };

export const SetInitialPasswordPage = () => {
  const navigate = useNavigate();
  const { userId, token } = useParams();

  const { mutate, data, isPending } = useMutation({
    mutationKey: ['set_password'],
    mutationFn: (password: string) => {
      return setPassword(userId, token, password);
    },
    onSuccess(response) {
      const error = response?.error;

      if (error) {
        if (error?.errorContent === ACTIVATION_TOKEN_INVALID_RESPONSE) {
          navigate(getSetPasswordLinkExpiredPath());
        }

        return;
      }

      amplitudeService.logEvent(AMPLITUDE_EVENTS.SetPasswordSuccess);

      navigate(getPasswordChangedPath());
    },
  });

  useEffect(() => {
    amplitudeService.logEvent(AMPLITUDE_EVENTS.SetInitialPasswordRedirect);
  }, []);

  const formik = useFormik<PasswordConfirmationFormType>({
    initialValues: { password: '', passwordConfirmation: '' },
    onSubmit: (values) => {
      mutate(values.password);
    },
    enableReinitialize: true,
    validationSchema: passwordValidationSchema,
  });

  const error = getResponseError(data?.error);

  return (
    <AuthLayout>
      <AuthStep>
        <AuthTitle>Set Your Password</AuthTitle>

        {error && (
          <Notification variant='error' marginBottom='24px'>
            {error}
          </Notification>
        )}

        <SetPasswordForm formik={formik} apiError={data?.error} isLoading={isPending} />
      </AuthStep>
    </AuthLayout>
  );
};
